/**
 * Iter 67em — Admin tile: AI chat funnel analytics.
 *
 * Powered by `/api/admin/chat-analytics`. Shows:
 *   • 4-step funnel: opens → messages sent → handoffs → confirmed bookings
 *   • Deflection ratio + engagement ratio
 *   • Top 10 most-asked questions (great FAQ-curation backlog)
 *   • Locale breakdown to spot which languages need more FAQ entries
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, TrendingUp, Globe2, ArrowRight, Quote, Sparkles, Check, Loader2 } from "lucide-react";

const WINDOWS = [
  { key: 7, label: "7d" },
  { key: 30, label: "30d" },
  { key: 90, label: "90d" },
];

function _pct(n) {
  if (n == null) return "—";
  return `${Math.round(n * 100)}%`;
}

export default function AdminChatAnalyticsTile() {
  const [data, setData] = useState(null);
  const [windowDays, setWindowDays] = useState(30);
  const [promoteState, setPromoteState] = useState({}); // {question: "loading"|"done"|"error"}

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/chat-analytics", { params: { days: windowDays, top_questions: 10 } })
      .then(({ data: payload }) => { if (!cancelled) setData(payload); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [windowDays]);

  /**
   * Iter 67en — Promote a top question into a multilingual public FAQ entry.
   * Calls the LLM (generate English answer + translate to 12 locales),
   * leaves the new row as a DRAFT so admin can review & publish from
   * AdminFaqsPanel.
   */
  const promote = async (q) => {
    setPromoteState((s) => ({ ...s, [q.question]: "loading" }));
    try {
      const { data: res } = await api.post("/admin/chat-faqs/promote", {
        question: q.question,
        popularity_count: q.count,
      });
      setPromoteState((s) => ({ ...s, [q.question]: "done" }));
      if (res.deduped) {
        toast.info("Question is already an FAQ — popularity count updated.");
      } else {
        toast.success("FAQ drafted in 13 languages. Publish from the FAQ panel below.");
      }
    } catch (e) {
      setPromoteState((s) => ({ ...s, [q.question]: "error" }));
      toast.error(e?.response?.data?.detail || "Couldn't promote — try again.");
    }
  };

  if (!data) {
    return (
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="admin-chat-analytics-loading">
        <div className="text-sm text-neutral-500">Loading chat analytics…</div>
      </div>
    );
  }

  const f = data.funnel || {};
  const opens = f.chat_opened || 0;
  const msgs = f.message_sent || 0;
  const handoffs = f.handoff_clicked || 0;
  const conversions = f.confirmed_bookings_from_chat || 0;

  const topQuestions = data.top_questions || [];
  const locales = data.locales || [];

  return (
    <div
      className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-white p-5"
      data-testid="admin-chat-analytics"
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-indigo-100 ring-1 ring-indigo-200 flex items-center justify-center shrink-0">
          <MessageCircle className="h-5 w-5 text-indigo-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg font-semibold">AI chat funnel</div>
          <p className="text-xs text-neutral-600 mt-0.5">
            How well the support bot is deflecting questions and converting curious renters into bookings.
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0" data-testid="admin-chat-analytics-windows">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => setWindowDays(w.key)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition ${windowDays === w.key ? "bg-indigo-600 text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}
              data-testid={`admin-chat-analytics-window-${w.key}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* Funnel */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="admin-chat-analytics-funnel">
        <FunnelStep label="Opened" value={opens} accent="text-sky-600" />
        <FunnelStep label="Sent message" value={msgs} accent="text-violet-600" sub={opens ? `${Math.round((msgs / opens) * 100)}% of opens` : null} />
        <FunnelStep label="Handoff to human" value={handoffs} accent="text-amber-600" sub={msgs ? `${Math.round((handoffs / msgs) * 100)}% of messages` : null} />
        <FunnelStep label="Confirmed booking" value={conversions} accent="text-emerald-600" sub={opens ? `${Math.round((conversions / opens) * 100)}% of opens` : null} />
      </div>

      {/* Health pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-semibold text-emerald-800">
          <TrendingUp className="h-3 w-3" />
          Deflection {_pct(data.deflection_ratio)}
        </div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-50 border border-violet-200 text-[11px] font-semibold text-violet-800">
          <MessageCircle className="h-3 w-3" />
          Engagement {_pct(data.engagement_ratio)}
        </div>
      </div>

      {/* Top questions */}
      <div className="mb-3">
        <div className="text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-2">Top questions</div>
        {topQuestions.length === 0 ? (
          <div className="text-xs text-neutral-500 italic">No questions yet in this window.</div>
        ) : (
          <ol className="space-y-1.5" data-testid="admin-chat-analytics-top-questions">
            {topQuestions.map((q) => {
              const state = promoteState[q.question];
              return (
                <li key={q.question} className="flex items-start gap-2.5 text-sm group">
                  <span className="font-display text-lg font-semibold text-amber-600 tabular-nums shrink-0 w-8 text-right">{q.count}</span>
                  <Quote className="h-3 w-3 text-neutral-300 mt-1 shrink-0" />
                  <span className="text-neutral-800 truncate flex-1" title={q.question}>{q.question}</span>
                  {q.locales.length > 0 && (
                    <span className="text-[10px] uppercase tracking-wider text-neutral-400 font-semibold shrink-0 hidden sm:inline">{q.locales.join(" · ")}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => promote(q)}
                    disabled={state === "loading" || state === "done"}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition shrink-0 disabled:opacity-60 disabled:cursor-not-allowed bg-violet-50 hover:bg-violet-100 text-violet-800 border border-violet-200"
                    title="Generate a public FAQ answer for this question (English + 12 translations)"
                    data-testid={`admin-chat-analytics-promote-${q.question.slice(0, 24)}`}
                  >
                    {state === "loading" && <Loader2 className="h-3 w-3 animate-spin" />}
                    {state === "done" && <Check className="h-3 w-3" />}
                    {!state && <Sparkles className="h-3 w-3" />}
                    {state === "loading" ? "Drafting…" : state === "done" ? "Drafted" : "Promote to FAQ"}
                  </button>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* Locales */}
      {locales.length > 0 && (
        <div className="border-t border-neutral-200 pt-3">
          <div className="text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-2 inline-flex items-center gap-1">
            <Globe2 className="h-3 w-3" /> Languages
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="admin-chat-analytics-locales">
            {locales.map((l) => (
              <span key={l.locale} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neutral-100 text-[11px] font-semibold text-neutral-700">
                <span className="uppercase">{l.locale}</span>
                <span className="text-neutral-500 tabular-nums">{l.messages}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FunnelStep({ label, value, sub, accent = "text-neutral-700" }) {
  return (
    <div className="rounded-xl border border-border bg-white p-3 relative">
      <div className={`font-display text-2xl font-semibold tabular-nums ${accent}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mt-0.5">{label}</div>
      {sub && (
        <div className="text-[10px] text-neutral-400 mt-1 inline-flex items-center gap-0.5">
          <ArrowRight className="h-2.5 w-2.5" /> {sub}
        </div>
      )}
    </div>
  );
}
